"use client"

import { zodResolver } from "@hookform/resolvers/zod";
import { NuisibleCreateSchema, NuisibleFormData } from "@repo/types";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useCreateNuisible, useNuisible, useUpdateNuisible } from "../../../hooks/api/nuisibles";

interface UseNuisibleFormProps {
  id?: string;
  nuisibleId?: number;
  onSuccess: () => void;
}

export function useNuisibleForm({ id, nuisibleId, onSuccess }: UseNuisibleFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!nuisibleId;

  // API hooks
  const { data: nuisibleData } = useNuisible(nuisibleId);
  const createNuisibleMutation = useCreateNuisible();
  const updateNuisibleMutation = useUpdateNuisible();

  // Form setup
  const form = useForm<NuisibleFormData>({
    //@ts-ignore
    resolver: zodResolver(NuisibleCreateSchema),
    defaultValues: {
      name: '',
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (isEditing && nuisibleData?.data) {
      form.reset({
        name: nuisibleData.data.name || '',
      });
    }
  }, [nuisibleData, form, isEditing]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const formData = form.getValues();

      if (isEditing && nuisibleId) {
        await updateNuisibleMutation.mutateAsync({
          id: nuisibleId,
          data: {
            id: nuisibleId,
            name: formData.name,
          },
        });
      } else {
        await createNuisibleMutation.mutateAsync(formData);
      }

      onSuccess();
    } catch (err: any) {
      console.error('Erreur lors de la soumission:', err);

      // Handle validation errors from the API
      if (err?.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        Object.keys(apiErrors).forEach((field) => {
          if (field in form.getValues()) {
            form.setError(field as keyof NuisibleFormData, {
              type: 'server',
              message: apiErrors[field],
            });
          }
        });
        setError(err.response.data.message || 'Erreurs de validation');
      } else {
        setError(
          err?.response?.data?.message ||
          err?.message ||
          'Une erreur inattendue s\'est produite'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isSubmitting,
    error,
    isEditing,
    onSubmit,
  };
}
